import Image from 'next/image'
import Link from 'next/link'
import { RateCtaLabel, StarRating } from '@/components/star-rating'
import { getEntityColorScheme } from '@/lib/park-colors'

export interface RatingCardEntity {
  id: string
  slug: string
  name: string
  imageUrl: string | null
  externalId?: string
  parent?: {
    name: string
    externalId: string
    parent?: {
      externalId: string
      parent?: {
        externalId: string
        parent?: any
      } | null
    } | null
  } | null
}

interface RatingCardProps {
  entity: RatingCardEntity
  rating: number
  totalRatings?: number
  /** Optional user-rating block to render below the card body. The
   *  comment text itself is optional — when absent, the block still
   *  shows the user's name, avatar, rating, and date. */
  comment?: {
    userName: string | null
    userImage?: string | null
    text?: string | null
    ratedAt: Date | string
    overallRating?: number
  }
}

export function RatingCard({ entity, rating, totalRatings, comment }: RatingCardProps) {
  const colorScheme = getEntityColorScheme(entity as any)
  const locationName = entity.parent?.name ?? null

  return (
    <Link
      href={`/rate/${entity.slug}`}
      className={`group flex flex-col bg-white border ${colorScheme.border} rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow`}
    >
      {/* Image */}
      <div className="relative w-full aspect-video bg-slate-100">
        <Image
          src={entity.imageUrl || '/no_image.png'}
          alt={entity.name}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-300"
          unoptimized
        />
      </div>

      {/* Body */}
      <div className={`flex items-start justify-between gap-3 px-4 py-3 ${colorScheme.bg}`}>
        <div className="min-w-0 flex-1">
          <h3 className={`font-semibold ${colorScheme.text} group-hover:underline break-words`}>
            {entity.name}
          </h3>
          {locationName && (
            <p className={`text-xs ${colorScheme.text} opacity-80 break-words`}>
              {locationName}
            </p>
          )}
        </div>
        <div className="flex-shrink-0">
          <StarRating
            rating={rating}
            size="sm"
            showNumber
            totalRatings={totalRatings}
          />
        </div>


      </div>

      <div className={`flex items-start justify-between gap-3 px-4 py-3 ${colorScheme.bg}`}>
        <RateCtaLabel />
      </div>

      {/* Optional user comment */}
      {comment && (
        <div className="px-4 py-3 border-t border-slate-200 bg-white">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {comment.userImage ? (
                <Image
                  src={comment.userImage}
                  alt={comment.userName ?? 'User'}
                  width={24}
                  height={24}
                  className="rounded-full"
                  unoptimized
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs text-slate-600">
                  {(comment.userName ?? 'U').charAt(0).toUpperCase()}
                </div>
              )}
              <span className="text-xs font-medium text-slate-700 break-words">
                {comment.userName ?? 'Anonymous'}
              </span>
            </div>
            {typeof comment.overallRating === 'number' && (
              <div className="flex flex-col items-end flex-shrink-0">
                <StarRating rating={comment.overallRating} size="sm" showNumber variant="user" />
                <span className="text-xs text-slate-400 mt-0.5">
                  {new Date(comment.ratedAt).toLocaleDateString()}
                </span>
              </div>
            )}

          </div>
          {comment.text && (
            <p className="text-sm text-slate-700 break-words">
              {comment.text}
            </p>
          )}
        </div>
      )}

    </Link>
  )
}
